import React from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import App from "./App";
import { AuthProvider } from "./lib/auth";
import ClientPortal from "./components/portals/ClientPortal";
import AdminPortal from "./components/portals/AdminPortal";
import AdminClientView from "./components/portals/AdminClientView";
import BookingPage from "./components/scheduling/BookingPage";
import ClientLogin from "./components/auth/ClientLogin";
import LegalPage from "./components/LegalPage";
import NotFound from "./components/NotFound";
import ErrorBoundary from "./components/common/ErrorBoundary";
import IntakeFormWrapper from "./components/IntakeFormWrapper";
import IntakeEmailEntry from "./components/IntakeEmailEntry";
import IntakeVerify from "./components/auth/IntakeVerify";
import SubmissionSuccess from "./components/SubmissionSuccessV2";
import BookingSuccess from "./components/payment/BookingSuccess";
import { ContentCatalog } from "./components/ContentCatalog";
import { PurchaseSuccess } from "./components/PurchaseSuccess";
import { PersonalizedPage } from "./components/PersonalizedPage";
import { CommunityPage } from "./components/CommunityPage";
import SoftwarePage from "./components/SoftwarePage";
import AuthCallback from "./src/pages/AuthCallback";
import DevPage from "./components/DevPage";
import { EnhancedDevUserSwitcher } from "./components/EnhancedDevUserSwitcher";
import { ROUTES } from "./src/constants/routes";

const router = createBrowserRouter(
  [
    // Homepage
    {
      path: ROUTES.HOME,
      element: <App />,
    },

    // Development page
    {
      path: "/dev",
      element: <DevPage />,
    },

    // Authentication
    {
      path: ROUTES.AUTH.CALLBACK,
      element: <AuthCallback />,
    },

    // Client onboarding flow - new structure
    {
      path: ROUTES.GET_STARTED.INTAKE,
      element: (
        <ErrorBoundary>
          <IntakeEmailEntry />
        </ErrorBoundary>
      ),
    },
    {
      path: ROUTES.GET_STARTED.VERIFY,
      element: (
        <ErrorBoundary>
          <IntakeVerify />
        </ErrorBoundary>
      ),
    },
    {
      path: ROUTES.GET_STARTED.ASSESSMENT,
      element: (
        <ErrorBoundary>
          <IntakeFormWrapper />
        </ErrorBoundary>
      ),
    },
    {
      path: ROUTES.GET_STARTED.COMPLETE,
      element: (
        <ErrorBoundary>
          <SubmissionSuccess />
        </ErrorBoundary>
      ),
    },

    // Client portal - new structure
    {
      path: ROUTES.PORTAL.LOGIN,
      element: <ClientLogin />,
    },
    {
      path: ROUTES.PORTAL.DASHBOARD,
      element: (
        <ErrorBoundary>
          <ClientPortal />
        </ErrorBoundary>
      ),
    },
    {
      path: ROUTES.PORTAL.RESOURCES,
      element: (
        <ErrorBoundary>
          <PersonalizedPage />
        </ErrorBoundary>
      ),
    },

    // Healer admin area - new structure
    {
      path: ROUTES.HEALER.DASHBOARD,
      element: <AdminPortal />,
    },
    {
      path: ROUTES.HEALER.CLIENT_DETAIL,
      element: (
        <ErrorBoundary>
          <AdminClientView />
        </ErrorBoundary>
      ),
    },

    // Public booking
    {
      path: ROUTES.SCHEDULE.BOOK,
      element: <BookingPage />,
    },
    {
      path: ROUTES.SCHEDULE.CONFIRMED,
      element: (
        <ErrorBoundary>
          <BookingSuccess />
        </ErrorBoundary>
      ),
    },

    // Public resources
    {
      path: ROUTES.RESOURCES.CATALOG,
      element: (
        <ErrorBoundary>
          <ContentCatalog />
        </ErrorBoundary>
      ),
    },
    {
      path: ROUTES.RESOURCES.COMMUNITY,
      element: (
        <ErrorBoundary>
          <CommunityPage />
        </ErrorBoundary>
      ),
    },
    {
      path: ROUTES.RESOURCES.TOOLS,
      element: (
        <ErrorBoundary>
          <SoftwarePage />
        </ErrorBoundary>
      ),
    },

    // Checkout
    {
      path: ROUTES.CHECKOUT.SUCCESS,
      element: (
        <ErrorBoundary>
          <PurchaseSuccess />
        </ErrorBoundary>
      ),
    },

    // Legal pages
    {
      path: ROUTES.LEGAL.BASE + "/:page",
      element: <LegalPage />,
    },

    // Legacy route redirects
    {
      path: "/client",
      element: <Navigate to={ROUTES.PORTAL.LOGIN} replace />,
    },
    {
      path: "/client/portal",
      element: <Navigate to={ROUTES.PORTAL.DASHBOARD} replace />,
    },
    {
      path: "/intake",
      element: <Navigate to={ROUTES.GET_STARTED.INTAKE} replace />,
    },
    {
      path: "/intake-form",
      element: <Navigate to={ROUTES.GET_STARTED.ASSESSMENT} replace />,
    },
    {
      path: "/auth/verify",
      element: <Navigate to={ROUTES.GET_STARTED.VERIFY} replace />,
    },
    {
      path: "/auth/verify-intake",
      element: <Navigate to={ROUTES.GET_STARTED.VERIFY} replace />,
    },
    {
      path: "/submission-success",
      element: <Navigate to={ROUTES.GET_STARTED.COMPLETE} replace />,
    },
    {
      path: "/admin",
      element: <Navigate to={ROUTES.HEALER.DASHBOARD} replace />,
    },
    {
      path: "/admin/client/:id",
      element: <Navigate to={ROUTES.HEALER.CLIENT_DETAIL} replace />,
    },
    {
      path: "/book",
      element: <Navigate to={ROUTES.SCHEDULE.BOOK} replace />,
    },
    {
      path: "/booking-success",
      element: <Navigate to={ROUTES.SCHEDULE.CONFIRMED} replace />,
    },
    {
      path: "/catalog",
      element: <Navigate to={ROUTES.RESOURCES.CATALOG} replace />,
    },
    {
      path: "/catalog/purchase-success",
      element: <Navigate to={ROUTES.CHECKOUT.SUCCESS} replace />,
    },
    {
      path: "/personalized",
      element: <Navigate to={ROUTES.PORTAL.RESOURCES} replace />,
    },
    {
      path: "/community",
      element: <Navigate to={ROUTES.RESOURCES.COMMUNITY} replace />,
    },
    {
      path: "/software",
      element: <Navigate to={ROUTES.RESOURCES.TOOLS} replace />,
    },

    // 404 handler
    {
      path: "*",
      element: <NotFound />,
    },
  ],
  {
    basename: "/",
  }
);

const Router: React.FC = () => {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
};

export default Router;
